import { FormFieldConfig, FormFieldType } from '../../../interface';
import { FilterSectionConfig } from './previous-cases-filters.interface';
import { PAYER_TYPE_FILTER_OPTIONS } from '../../../constants/payer-type-filter-options';

export const DEBOUNCE_MS = 400;

export const SOURCES_KEY = 'sources';
export const FACILITIES_KEY = 'facilities';
export const HOSPITAL_KEY = 'hospital';
export const HAS_UPDATES_KEY = 'hasUpdates';
export const HAS_UPDATES_LABEL = 'Has updates only';
export const HAS_UPDATES_DESCRIPTION = 'Only referrals that changed since you last viewed them.';
export const EXPAND_ALL_LABEL = 'Expand All';

export const SOURCES_SECTION: FilterSectionConfig = {
  key: SOURCES_KEY,
  title: 'Sources',
  countNoun: 'sources',
  description: 'Which referral platforms to include. All are selected by default.',
  options: [],
  searchable: true,
  selectAll: true,
};

export const FACILITIES_SECTION: FilterSectionConfig = {
  key: FACILITIES_KEY,
  title: 'Facilities',
  countNoun: 'facilities',
  description: 'Limit to referrals sent to these facilities.',
  options: [],
  searchable: true,
  selectAll: true,
};

const STATUS_SECTIONS: FilterSectionConfig[] = [
  {
    key: 'snfStatus',
    title: 'SNF Status',
    tone: 'status',
    showSelectedOptions: true,
    description: "Your facility's response on the referral.",
    options: [
      { value: 'new', label: 'New', helper: 'No response sent yet' },
      { value: 'inProgress', label: 'Considering', helper: 'Being reviewed' },
      { value: 'accepted', label: 'Accepted', helper: 'Marked as accepted' },
      { value: 'declined', label: 'Declined', helper: 'Marked as declined' },
    ],
  },
  {
    key: 'hospitalStatus',
    title: 'Hospital Status',
    tone: 'status',
    showSelectedOptions: true,
    description: 'Whether the hospital chose your facility.',
    options: [
      { value: 'selected', label: 'Selected', helper: 'Hospital picked your facility' },
      { value: 'notSelected', label: 'Not Selected', helper: 'Hospital picked another facility' },
    ],
  },
  {
    key: 'admitted',
    title: 'Admitted',
    tone: 'status',
    showSelectedOptions: true,
    description: 'Whether the patient has been admitted to your census.',
    options: [
      { value: 'yes', label: 'Yes', helper: 'Patient is on your census' },
      { value: 'no', label: 'No', helper: 'Not admitted yet' },
    ],
  },
];

const HOSPITAL_SECTION: FilterSectionConfig = {
  key: HOSPITAL_KEY,
  title: 'Hospital',
  countNoun: 'hospitals',
  description: 'The referring hospital.',
  options: [],
  searchable: true,
  selectAll: true,
};

function coverageSectionForClient(): FilterSectionConfig[] {
  if (PAYER_TYPE_FILTER_OPTIONS.length === 0) return [];
  return [{
    key: 'coverage',
    title: 'Payer Type',
    description: 'Payer type of the primary payer on the referral.',
    options: PAYER_TYPE_FILTER_OPTIONS.map(({ id, label }) => ({ value: id, label })),
  }];
}

export const FILTER_SECTIONS: FilterSectionConfig[] = [
  SOURCES_SECTION,
  FACILITIES_SECTION,
  HOSPITAL_SECTION,
  ...STATUS_SECTIONS,
  ...coverageSectionForClient(),
];

// Section key -> grid colId of the column that hosts its header filter; sections missing here stay in the toolbar.
export const SECTION_COLUMN_MAP: Record<string, string> = {
  [SOURCES_KEY]: 'srcType',
  [FACILITIES_KEY]: 'site',
  [HOSPITAL_KEY]: 'hospital',
  snfStatus: 'snfStatus',
  hospitalStatus: 'hospitalStatus',
  admitted: 'admitted',
};

export const DATE_RANGE_KEY = 'dateRange';
export const DATE_RANGE_LABEL = 'Date Range';

export const PANEL_FIELD_DESCRIPTIONS: Record<string, string> = {
  [DATE_RANGE_KEY]: 'Referrals whose first posting date falls inside the range.',
};

export const PANEL_FIELDS: FormFieldConfig[] = [
  {
    label: DATE_RANGE_LABEL,
    key: DATE_RANGE_KEY,
    type: FormFieldType.DateRange,
    placeholder: 'Select date range',
    // Date-only field: an hour preset starts that many calendar days back, so it covers at least the window.
    dateRangePresets: [
      { label: 'Last 24 hours', days: 1 },
      { label: 'Last 48 hours', days: 2 },
    ],
  },
];

export const SHOW_ARCHIVED_KEY = 'showArchived';

export const SHOW_ARCHIVED_LABEL = 'Show Archived';

export const HIGHLIGHT_NEW_LABEL = 'Highlight new referrals';

export const PATIENT_FILTERS_DEFAULT = { [SHOW_ARCHIVED_KEY]: true };
